import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthData {
  username: string;
  loginTime: string;
  isAuthenticated: boolean;
}

interface AuthContextType {
  authData: AuthData | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authData, setAuthData] = useState<AuthData | null>(null);

  useEffect(() => {
    // Check for existing auth session on mount
    const storedAuth = localStorage.getItem('garmentBudgetAuth');
    if (storedAuth) {
      try {
        const parsedAuth = JSON.parse(storedAuth);
        if (parsedAuth.isAuthenticated) {
          setAuthData(parsedAuth);
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
        localStorage.removeItem('garmentBudgetAuth');
      }
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    // Demo credentials
    const DEMO_CREDENTIALS = [
      { username: 'demo', password: 'demo123' },
      { username: 'admin', password: 'admin123' },
      { username: 'user', password: 'user123' },
    ];

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    const isValid = DEMO_CREDENTIALS.some(
      cred => cred.username === username && cred.password === password
    );

    if (isValid) {
      const newAuthData: AuthData = {
        username,
        loginTime: new Date().toISOString(),
        isAuthenticated: true,
      };
      setAuthData(newAuthData);
      localStorage.setItem('garmentBudgetAuth', JSON.stringify(newAuthData));
      return true;
    }

    return false;
  };

  const logout = () => {
    setAuthData(null);
    localStorage.removeItem('garmentBudgetAuth');
  };

  const isAuthenticated = (): boolean => {
    return authData?.isAuthenticated || false;
  };

  const value: AuthContextType = {
    authData,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
